import cleanDeep from 'clean-deep';
import { graphql, useStaticQuery } from 'gatsby';
import React, { FC } from 'react';

import { queryParams } from '@/blocks/player-search-result-block/queryParams';
import {
  Card,
  CardCloseButton,
  CardOptions,
  CountryField,
  DateField,
  DateTimeField,
  EmailField,
  Form,
  IPAddressField,
  NumberField,
  PhoneNumberField,
  SelectField,
  SubmitButton,
  TextField,
  useModalContext,
} from '@/components';
import { useTranslate } from '@/contexts';
import { AccountStatusEnum, RegistrationStatusFilter } from '@/globalTypes';
import { useQueryParams } from '@/hooks';
import { useBrandValueOptions, useBrands } from '@/hooks/useBrands';

const query = graphql`
  query SanityAdvancedPlayerForm {
    sanityAdvancedPlayerSearchForm {
      title {
        ...SanityLocaleString
      }
      firstName {
        ...SanityLocaleString
      }
      lastName {
        ...SanityLocaleString
      }
      phoneNumber {
        ...SanityLocaleString
      }
      email {
        ...SanityLocaleString
      }
      minBalance {
        ...SanityLocaleString
      }
      maxBalance {
        ...SanityLocaleString
      }
      birthDate {
        ...SanityLocaleString
      }
      countryCode {
        ...SanityLocaleString
      }
      lastLoginIp {
        ...SanityLocaleString
      }
      brand {
        ...SanityLocaleString
      }
      accountStatus {
        ...SanityLocaleString
      }
      registrationStatus {
        ...SanityLocaleString
      }
      ssn {
        ...SanityLocaleString
      }
      registeredIp {
        ...SanityLocaleString
      }
      registeredIpCountryCode {
        ...SanityLocaleString
      }
      lastLoginFrom {
        ...SanityLocaleString
      }
      lastLoginTo {
        ...SanityLocaleString
      }
      lastLoginIpCountryCode {
        ...SanityLocaleString
      }
      street {
        ...SanityLocaleString
      }
      city {
        ...SanityLocaleString
      }
      zipCode {
        ...SanityLocaleString
      }
      submit {
        ...SanityLocaleString
      }
    }
  }
`;

const AdvancedPlayerSearchForm: FC = () => {
  const data = useStaticQuery<Queries.SanityAdvancedPlayerFormQuery>(query);
  const [defaultValues, setQuery] = useQueryParams(queryParams);

  const { brands } = useBrands();
  const brandValueOptions = useBrandValueOptions(brands);

  const form = data.sanityAdvancedPlayerSearchForm;

  const { close } = useModalContext();

  const { t } = useTranslate();

  const onFilter = (data: typeof defaultValues) => {
    const cleaned = cleanDeep(data);

    // Filter out null values and convert to undefined for type compatibility
    const clean = Object.entries(cleaned).reduce(
      (acc, [key, value]) => {
        if (value !== null) {
          acc[key] = value;
        }

        return acc;
      },
      {} as Record<string, any>,
    );

    setQuery(clean, 'pushIn');

    if (close) {
      close();
    }
  };

  if (!form) {
    return null;
  }

  return (
    <Card
      size="lg"
      title="Advanced Search"
      options={
        <CardOptions>
          <CardCloseButton />
        </CardOptions>
      }
    >
      <div className="p-3">
        <Form
          defaultValues={defaultValues}
          onSubmit={onFilter}
          className="grid grid-cols-2 sm:grid-cols-3 gap-6"
        >
          <TextField
            title={t(form.firstName)}
            name="firstName"
            id="AdvancedPlayerSearchForm__firstName"
            autoFocus
          />
          <TextField
            title={t(form.lastName)}
            name="lastName"
            id="AdvancedPlayerSearchForm__lastName"
          />
          <PhoneNumberField
            title={t(form.phoneNumber)}
            name="phoneNumber"
            id="AdvancedPlayerSearchForm__phoneNumber"
          />
          <EmailField
            title={t(form.email)}
            name="email"
            id="AdvancedPlayerSearchForm__email"
          />
          <NumberField
            title={t(form.minBalance)}
            name="minBalance"
            id="AdvancedPlayerSearchForm__minBalance"
          />
          <NumberField
            title={t(form.maxBalance)}
            name="maxBalance"
            id="AdvancedPlayerSearchForm__maxBalance"
          />
          <DateField
            title={t(form.birthDate)}
            name="birthDate"
            id="AdvancedPlayerSearchForm__birthDate"
          />
          <CountryField
            title={t(form.countryCode)}
            name="countryCode"
            id="AdvancedPlayerSearchForm__countryCode"
          />
          <IPAddressField
            title={t(form.lastLoginIp)}
            name="lastLoginIp"
            id="AdvancedPlayerSearchForm__lastLoginIp"
          />
          <SelectField
            title={t(form.brand)}
            name="brand"
            id="AdvancedPlayerSearchForm__brand"
            options={brandValueOptions.options}
          />
          <SelectField
            title={t(form.accountStatus)}
            name="accountStatus"
            id="AdvancedPlayerSearchForm__accountStatus"
            options={Object.keys(AccountStatusEnum).map((value) => ({
              value,
              label: value,
            }))}
          />
          <SelectField
            title={t(form.registrationStatus)}
            name="registrationStatus"
            id="AdvancedPlayerSearchForm__registrationStatus"
            options={Object.keys(RegistrationStatusFilter).map((value) => ({
              value,
              label: value,
            }))}
          />
          <TextField
            title={t(form.ssn)}
            name="ssn"
            id="AdvancedPlayerSearchForm__ssn"
          />
          <TextField
            title={t(form.registeredIp)}
            name="registeredIp"
            id="AdvancedPlayerSearchForm__registeredIp"
          />
          <CountryField
            title={t(form.registeredIpCountryCode)}
            name="registeredIpCountryCode"
            id="AdvancedPlayerSearchForm__registeredIpCountryCode"
          />
          <DateTimeField
            title={t(form.lastLoginFrom)}
            name="lastLoginFrom"
            id="AdvancedPlayerSearchForm__lastLoginFrom"
          />
          <DateTimeField
            title={t(form.lastLoginTo)}
            name="lastLoginTo"
            id="AdvancedPlayerSearchForm__lastLoginTo"
          />
          <IPAddressField
            title={t(form.lastLoginIpCountryCode)}
            name="lastLoginIpCountryCode"
            id="AdvancedPlayerSearchForm__lastLoginIpCountryCode"
          />
          <TextField
            title={t(form.street)}
            name="street"
            id="AdvancedPlayerSearchForm__street"
          />
          <TextField
            title={t(form.city)}
            name="city"
            id="AdvancedPlayerSearchForm__city"
          />
          <TextField
            title={t(form.zipCode)}
            name="zipCode"
            id="AdvancedPlayerSearchForm__zipCode"
          />
          <SubmitButton value={t(form.submit)} />
        </Form>
      </div>
    </Card>
  );
};

export default AdvancedPlayerSearchForm;
